import { UserState } from '../reducers/userSlice';
import {
  DISPLAY_PREFERENCE_VERSION,
  DisplayPreference,
  parseDisplayPreference,
} from './displayPreference';
import { TaskRouterLocalStorageInfo } from '../redux/features/taskRouter/taskRouter.props';
import logger from './logger';
import { nullOrUndefined } from './types';

const USER_STATE_KEY = 'user-info';
const USER_STATE_VERSION_KEY = 'user-info-version';
const AUTH_TOKEN_KEY = 'auth-token';
const TASKROUTER_AGENT_INFO = 'task-router-agent-info';
const LAYOUT_PREFERENCE_KEY = 'hitl-layout-preference';
// Card-height preferences live under their OWN dedicated keys, kept separate
// from the pane-width key above so the proven width path stays untouched (no
// versioning, no back-compat parsing, no risk of overwriting an existing width
// choice). Each stack is stored independently as a [top, bottom] percentage
// pair summing to 100 — the left pane (Transcript / AI Copilot) and the right
// pane (Agent Cart / POS Cart).
const LAYOUT_HEIGHT_LEFT_KEY = 'hitl-layout-height-left';
const LAYOUT_HEIGHT_RIGHT_KEY = 'hitl-layout-height-right';
/**
 * HITL-1648 — the My Cart | Live order column split inside the right pane.
 *
 * Its own key rather than a third element on the right stack's: that one is a
 * HEIGHT split between two cards, this is a WIDTH split between two columns
 * inside one of them. They resize independently and a shared key would make one
 * silently clobber the other.
 */
const LAYOUT_SPLIT_CARTS_KEY = 'hitl-layout-split-carts';
/**
 * HITL-1695 — the agent's display choices (transcript format, centre-pane
 * look). Its own key: these are not sizes, and folding them into a layout key
 * would make "Reset" on one clear the other by accident rather than by design.
 */
const DISPLAY_PREFERENCE_KEY = 'hitl-display-preference';

export const saveAuthToken = (authToken: string | nullOrUndefined) => {
  if (authToken) {
    localStorage.setItem(AUTH_TOKEN_KEY, authToken);
  } else {
    localStorage.removeItem(AUTH_TOKEN_KEY);
  }
};

export const getAuthToken = (): string | undefined => {
  const authToken = localStorage.getItem(AUTH_TOKEN_KEY);
  if (authToken && authToken !== '') {
    return authToken;
  }
  return undefined;
};

// Change this versionNumber whenever the format for user state changes so we know to delete the old data
const userStateVersion = '1.0.0';
export const getUserState = (): UserState | undefined => {
  // If called on server, localStorage doesn't exist
  if (typeof window === 'undefined') {
    return undefined;
  }
  const serializedState = localStorage.getItem(USER_STATE_KEY);
  if (serializedState === null) {
    return undefined;
  }
  const foundUserStateVersion = localStorage.getItem(USER_STATE_VERSION_KEY);
  if (!foundUserStateVersion || foundUserStateVersion !== userStateVersion) {
    logger.warn('User state version is out of date. Disregarding');
    localStorage.removeItem(USER_STATE_KEY);
    return undefined;
  }
  const userState = JSON.parse(serializedState);
  if (userState) {
    return userState;
  }
  return undefined;
};

export const saveUserState = (userState: UserState) => {
  const serializedState = JSON.stringify(userState);
  localStorage.setItem(USER_STATE_KEY, serializedState);
  localStorage.setItem(USER_STATE_VERSION_KEY, userStateVersion);
};

export const saveTaskRouterInfoInLS = (
  taskRouterInfo: TaskRouterLocalStorageInfo
) => {
  const serializedState = JSON.stringify(taskRouterInfo);
  localStorage.setItem(TASKROUTER_AGENT_INFO, serializedState);
};

export const getTaskRouterInfoFromLS = () => {
  const serializedState: any =
    localStorage.getItem(TASKROUTER_AGENT_INFO) || null;
  return JSON.parse(serializedState);
};

export const updateTaskRouterInfoInLS = (obj: any) => {
  const oldValue = getTaskRouterInfoFromLS();
  const serializedState = JSON.stringify({ ...oldValue, ...obj });
  localStorage.setItem(TASKROUTER_AGENT_INFO, serializedState);
};

export const removeTaskRouterInfoFromLS = () => {
  localStorage.removeItem(TASKROUTER_AGENT_INFO);
};

export const getLoggedInUserEmail = () => {
  const userInfo = localStorage.getItem(USER_STATE_KEY) || '';
  return userInfo ? JSON.parse(userInfo).userProfile?.email : '';
};

export const getTaskRouterAgentId = () => {
  const agentInfo = localStorage.getItem(TASKROUTER_AGENT_INFO) || '';
  return agentInfo ? JSON.parse(agentInfo).agentId : '';
};

/**
 * Resolves the per-agent suffix used to scope both the resizable-panes storage
 * (`hitl-console-panes:<key>`) and the explicit layout preference below to the
 * signed-in user. Kept in one place so the two stay in lock-step.
 */
export const resolvePaneUserKey = (
  userProfile?: { username?: string; email?: string } | null
) => userProfile?.username || userProfile?.email || 'default';

/**
 * The agent's explicit pane split, as [left, middle, right] percentages summing
 * to 100. Written ONLY when the agent hits "Apply" in the Layout preference
 * dialog — react-resizable-panels auto-persists its own sizes on every layout
 * (including the default on first mount), so that store can't tell an explicit
 * choice from an auto-saved default. This dedicated key is that signal: when it
 * exists, the saved split is honoured on task arrival instead of the transcript
 * auto-expanding.
 */
export const getLayoutPreference = (
  userKey: string
): [number, number, number] | null => {
  try {
    const raw = localStorage.getItem(`${LAYOUT_PREFERENCE_KEY}:${userKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      Array.isArray(parsed) &&
      parsed.length === 3 &&
      parsed.every(
        (size) => typeof size === 'number' && Number.isFinite(size)
      ) &&
      Math.round(parsed.reduce((sum, size) => sum + size, 0)) === 100
    ) {
      return parsed as [number, number, number];
    }
  } catch (error) {
    logger.warn('Could not read layout preference from local storage');
  }
  return null;
};

export const saveLayoutPreference = (
  userKey: string,
  sizes: [number, number, number]
) => {
  localStorage.setItem(
    `${LAYOUT_PREFERENCE_KEY}:${userKey}`,
    JSON.stringify(sizes)
  );
};

export const removeLayoutPreference = (userKey: string) => {
  localStorage.removeItem(`${LAYOUT_PREFERENCE_KEY}:${userKey}`);
};

/**
 * A stacked-card height split, as [top, bottom] percentages summing to 100.
 * Validation: a 2-element array of finite numbers, each a valid percentage
 * (0–100, so a corrupt negative / >100 value can never resize a card), summing
 * to 100. The exact clamp (min/max height) is intentionally NOT enforced here —
 * the read stays lenient so any valid split is honoured; callers re-clamp.
 */
const readStackHeight = (
  storageKey: string,
  userKey: string
): [number, number] | null => {
  try {
    const raw = localStorage.getItem(`${storageKey}:${userKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      Array.isArray(parsed) &&
      parsed.length === 2 &&
      parsed.every(
        (size) =>
          typeof size === 'number' &&
          Number.isFinite(size) &&
          size >= 0 &&
          size <= 100
      ) &&
      Math.round(parsed.reduce((sum, size) => sum + size, 0)) === 100
    ) {
      return parsed as [number, number];
    }
  } catch (error) {
    logger.warn('Could not read card height preference from local storage');
  }
  return null;
};

const saveStackHeight = (
  storageKey: string,
  userKey: string,
  sizes: [number, number]
) => {
  try {
    localStorage.setItem(`${storageKey}:${userKey}`, JSON.stringify(sizes));
  } catch (error) {
    // Storage can throw (quota exceeded, private-mode restrictions). A dropped
    // height preference is non-critical — the layout just falls back to the
    // default next load — so swallow it rather than break the resize/dialog.
    logger.warn('Could not save card height preference to local storage');
  }
};

/** Left pane card heights [Transcript, AI Copilot]. */
export const getLeftStackHeight = (userKey: string) =>
  readStackHeight(LAYOUT_HEIGHT_LEFT_KEY, userKey);
export const saveLeftStackHeight = (userKey: string, sizes: [number, number]) =>
  saveStackHeight(LAYOUT_HEIGHT_LEFT_KEY, userKey, sizes);

/** Right pane card heights [Agent Cart, POS Cart]. */
export const getRightStackHeight = (userKey: string) =>
  readStackHeight(LAYOUT_HEIGHT_RIGHT_KEY, userKey);
export const saveRightStackHeight = (
  userKey: string,
  sizes: [number, number]
) => saveStackHeight(LAYOUT_HEIGHT_RIGHT_KEY, userKey, sizes);

/**
 * HITL-1648 — the two-cart column split [My Cart, Live order].
 *
 * Reuses the stack-height reader verbatim. The stored shape is identical — two
 * percentages summing to 100 — and the validation that matters (finite, 0-100,
 * sums to 100) is the same question whichever axis it describes. The per-column
 * clamp is re-applied by the caller, as it is for the height splits.
 */
export const getCartSplit = (userKey: string) =>
  readStackHeight(LAYOUT_SPLIT_CARTS_KEY, userKey);
export const saveCartSplit = (userKey: string, sizes: [number, number]) =>
  saveStackHeight(LAYOUT_SPLIT_CARTS_KEY, userKey, sizes);

/**
 * Clears every stored split (used by "Reset all to default").
 *
 * The cart split joins the two card heights here rather than getting its own
 * remove: "Reset all" means all, and a preference that survived it would be a
 * bug the agent could not explain.
 */
export const removeStackHeights = (userKey: string) => {
  localStorage.removeItem(`${LAYOUT_HEIGHT_LEFT_KEY}:${userKey}`);
  localStorage.removeItem(`${LAYOUT_HEIGHT_RIGHT_KEY}:${userKey}`);
  localStorage.removeItem(`${LAYOUT_SPLIT_CARTS_KEY}:${userKey}`);
};

/**
 * HITL-1695 — the agent's display preference, or null when none is saved.
 *
 * Null (not the defaults) so the caller can tell "never chosen" from "chose the
 * defaults"; `parseDisplayPreference` does the field-by-field fallback.
 */
export const getDisplayPreference = (
  userKey: string
): DisplayPreference | null => {
  try {
    const raw = localStorage.getItem(`${DISPLAY_PREFERENCE_KEY}:${userKey}`);
    if (!raw) return null;
    return parseDisplayPreference(JSON.parse(raw));
  } catch (error) {
    logger.warn('Could not read display preference from local storage');
  }
  return null;
};

export const saveDisplayPreference = (
  userKey: string,
  preference: DisplayPreference
) => {
  try {
    localStorage.setItem(
      `${DISPLAY_PREFERENCE_KEY}:${userKey}`,
      JSON.stringify({ v: DISPLAY_PREFERENCE_VERSION, ...preference })
    );
  } catch (error) {
    // Same reasoning as saveStackHeight: a dropped display choice falls back to
    // the defaults next load, which is not worth breaking the dialog over.
    logger.warn('Could not save display preference to local storage');
  }
};

export const removeDisplayPreference = (userKey: string) => {
  localStorage.removeItem(`${DISPLAY_PREFERENCE_KEY}:${userKey}`);
};
